'use client';

import {CalendarIcon} from '@rocket-ui/icons';
import {useEvent} from '@rocket-ui/react-tools';
import {date as dateTool} from '@rocket-ui/tools';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Assign} from 'utility-types';
import {NewIconButton} from '../Button';
import {NewCalendar} from '../DatePicker';
import {NewPopover, NewPopoverRef} from '../Popover';
import {MaskedInput} from './MaskedInput';
import {InputProps} from './types';

export type DateInputValue = Date | null;
export type DateInputOnChangeHandler = (date: Date | null) => void;

export type DateInputProps = Assign<
	Omit<InputProps<DateInputValue>, 'defaultValue' | 'placeholder' | 'dynamicPlaceholder'>,
	{value?: DateInputValue; minDate?: Date; maxDate?: Date; withPicker?: boolean; onChange?: DateInputOnChangeHandler}
>;

const formatDate = 'dd.MM.yyyy';

const getDateMask = (rawValue: string) => {
	const [day = '', month = ''] = rawValue.split('.');

	const mask: (string | RegExp)[] = [];

	// day
	mask.push(/[0-3]/);
	day[0] === '0' && mask.push(/[1-9]/);
	(day[0] === '1' || day[0] === '2') && mask.push(/[0-9]/);
	day[0] === '3' && mask.push(/[01]/);

	mask.push('.');

	// month
	mask.push(/[01]/);
	month[0] === '0' && mask.push(/[1-9]/);
	month[0] === '1' && mask.push(/[012]/);

	mask.push('.');

	// year
	mask.push(/[2]/, /\d/, /\d/, /\d/);

	return mask;
};

const getValue = (dateValue?: DateInputValue, value = '', changing?: boolean): string => {
	if (!dateValue) {
		return changing ? value : '';
	}

	return dateTool.format(dateValue, formatDate);
};

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
	{value: inputValue, onChange, onFocus, onBlur, minDate, maxDate, withPicker, ...props},
	ref,
) {
	const popoverRef = useRef<NewPopoverRef>(null);
	const [value, setValue] = useState<string>(getValue(inputValue));
	const changing = useRef(false);

	useEffect(() => {
		setValue((value) => getValue(inputValue, value, changing.current));
	}, [inputValue, setValue]);

	const dynamicPlaceholder = useMemo((): string => {
		const result = 'дд.мм.гггг'.replace(/./g, (sub, i) => value[i] || sub);

		return result === value ? '' : result;
	}, [value]);

	const handleChange = useEvent((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		let date = formatDate.length === value.length ? dateTool.parse(event.target.value, formatDate) : null;

		if (date) {
			if (minDate && dateTool.isBefore(date, minDate)) {
				date = minDate;
			}
			if (maxDate && dateTool.isAfter(date, maxDate)) {
				date = maxDate;
			}
		}

		onChange?.(date);
		setValue(value);
	});

	const handleFocus = useEvent((event: React.FocusEvent<HTMLInputElement>) => {
		changing.current = true;
		onFocus?.(event);
	});

	const handleBlur = useEvent((event: React.FocusEvent<HTMLInputElement>) => {
		changing.current = false;
		onBlur?.(event);
	});

	const handleCalendarSelectionChange = (date: Date) => {
		onChange?.(date);
		setValue(dateTool.format(date, formatDate));
		popoverRef.current?.close();
	};

	const classes = useMemo(() => ({
		...props.classes,
		nativeInput: [props.classes?.nativeInput, 'ios-input--no-16'].filter(Boolean).join(' '),
	}), [props.classes]);

	const inputComponent = (
		<MaskedInput
			ref={ref}
			mask={getDateMask}
			value={value}
			onChange={handleChange}
			dynamicPlaceholder={dynamicPlaceholder}
			onFocus={handleFocus}
			onBlur={handleBlur}
			{...props}
			classes={classes}
		/>
	);

	if (!withPicker) return inputComponent;

	return (
		<div className={'flex-1 flex gap-2 items-center'}>
			<div className={'flex-1 flex'}>{inputComponent}</div>

			<div className={'flex-none'}>
				<NewPopover handler={<NewIconButton icon={CalendarIcon} size={'xs'} variant={'ghost'} />} ref={popoverRef}>
					<NewCalendar
						className={'p-4'}
						value={inputValue || undefined}
						onChange={handleCalendarSelectionChange}
						minDate={minDate}
						maxDate={maxDate}
						highlightToday
					/>
				</NewPopover>
			</div>
		</div>
	);
});
